import { isExpectedAutohighlightsError } from '@confluence/content-unified-query/entry-points/isExpectedAutohighlightsError.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	classifyContentPermissionError,
	isContentAutohighlightsError,
	isUnsupportedCustomerAccountFieldError,
} from '@confluence/content-unified-query/entry-points/errors.ts';
import { fg } from '@confluence/feature-gating';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

/** Optional field failures must not block the requested page; unexpected failures need a local reporter. */
export const getViewPageContentError = (
	error: ApolloErrorInterop | undefined,
	contentId: string,
	resolvedContentId: string | null | undefined,
	onUnexpectedOptionalFieldError?: (error: ApolloErrorInterop) => void,
): ApolloErrorInterop | undefined => {
	// Never use missing content or the previous page's data to dismiss a permission failure.
	if (!error || !contentId || resolvedContentId !== contentId) {
		return error;
	}

	const unexpectedOptionalFieldErrors: Array<ApolloErrorInterop['graphQLErrors'][number]> = [];
	const graphQLErrors = error.graphQLErrors.filter((graphQLError) => {
		if (
			graphQLError.path?.[0] === 'agentStudio_getAgents' &&
			expValEquals('platform_editor_mention_rovo', 'isEnabled', true)
		) {
			// The identity lookup is optional; report it locally instead of failing the page.
			if (onUnexpectedOptionalFieldError) {
				unexpectedOptionalFieldErrors.push(graphQLError);
				return false;
			}
			return true;
		}
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (fg('confluence_shared_content_permission_errors')) {
			if (isContentAutohighlightsError(graphQLError, contentId, resolvedContentId)) {
				if (isExpectedAutohighlightsError(graphQLError)) {
					markErrorAsHandled(graphQLError);
					return false;
				}
				if (onUnexpectedOptionalFieldError) {
					unexpectedOptionalFieldErrors.push(graphQLError);
					return false;
				}
				return true;
			}
			if (
				classifyContentPermissionError(graphQLError, contentId, resolvedContentId) ===
				'optional-permission'
			) {
				markErrorAsHandled(graphQLError);
				return false;
			}
			return true;
		}
		if (
			(graphQLError.path?.[0] === 'contentAnalyticsViewers' && isUnauthorizedError(graphQLError)) ||
			(isUnsupportedCustomerAccountFieldError(graphQLError) &&
				expVal('confluence_exp_vp_document', 'isEnabled', false))
		) {
			markErrorAsHandled(graphQLError);
			return false;
		}
		return true;
	});

	if (unexpectedOptionalFieldErrors.length) {
		onUnexpectedOptionalFieldError?.(
			new ApolloErrorInterop({ graphQLErrors: unexpectedOptionalFieldErrors }),
		);
	}

	if (graphQLErrors.length === error.graphQLErrors.length) {
		return error;
	}
	if (!graphQLErrors.length && !error.networkError) {
		// Marking the wrapper recursively handles every field. Leave unexpected optional
		// errors unhandled until their local ErrorDisplay reports them.
		if (!unexpectedOptionalFieldErrors.length) {
			markErrorAsHandled(error);
		}
		return undefined;
	}

	return new ApolloErrorInterop({
		graphQLErrors,
		networkError: error.networkError,
		extraInfo: error.extraInfo,
	});
};
